import { useEffect, useRef, useState } from "react";
import { FiMusic } from "react-icons/fi";
import { studioOn } from "../studio/api";
import BandForTake from "./BandForTake";
import styles from "./social.module.css";

const MAX_SECONDS = 600, COUNT_IN = 3;
// Playing to a beat: the first flash comes this long after recording starts (so the recorder is
// surely running), and tempos run between these.
const LEAD_MS = 600, SLOWEST = 40, FASTEST = 240;
// Recording formats in order of preference (Safari and newer Chrome record MP4); Bunny takes both.
export const FORMATS = [["video/mp4;codecs=avc1.42E01E,mp4a.40.2", "mp4"], ["video/mp4", "mp4"], ["video/webm;codecs=vp9,opus", "webm"], ["video/webm;codecs=vp8,opus", "webm"], ["video/webm", "webm"]];
const FAILURES = {
  NotAllowedError: "TrueChops needs permission to use your camera and microphone. Allow it from your browser’s address bar, then try again.",
  SecurityError: "TrueChops needs permission to use your camera and microphone. Allow it from your browser’s address bar, then try again.",
  NotFoundError: "No camera or microphone was found.",
  NotReadableError: "Your camera or microphone is in use by another app. Close it and try again.",
  OverconstrainedError: "That camera or microphone isn’t available. Choose another one.",
};
const clock = (seconds) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;
export const canRecordVideo = () => typeof window !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia) && typeof window.MediaRecorder !== "undefined";

// Records a video with the camera and microphone and hands it over as a file to upload. The
// browser's echo cancellation, noise suppression and automatic gain are off, so drums sound the
// way they're played; the meter shows the input level (red when it's clipping).
// Played to a beat, a silent count-in of one bar flashes on screen, then the beat keeps flashing
// while you play, and TrueChops can add a band to the take afterwards (BandForTake.js), in time
// with you. `idea`: what the video goes with, for the tempo, meter and key ({ tempo, beats, keySig,
// words }).
export default function VideoRecorder({ maxBytes, onUse, onCancel, idea = {} }) {
  const live = useRef(null), meterBar = useRef(null), stream = useRef(null), recorder = useRef(null), meter = useRef(null), timers = useRef([]), startedAt = useRef(0);
  const plan = useRef(null), beatFrame = useRef(0), taps = useRef([]);
  const [state, setState] = useState("starting"), [error, setError] = useState("");
  const [devices, setDevices] = useState({ video: [], audio: [] }), [chosen, setChosen] = useState({ video: "", audio: "" }), [active, setActive] = useState({ video: "", audio: "", mirrored: true });
  const [count, setCount] = useState(COUNT_IN), [elapsed, setElapsed] = useState(0), [take, setTake] = useState(null);
  const [beatOn, setBeatOn] = useState(false), [tempo, setTempo] = useState(Math.round(idea.tempo) || 100), [beats, setBeats] = useState(idea.beats >= 2 && idea.beats <= 7 ? idea.beats : 4);
  const [beat, setBeat] = useState(-1), [banding, setBanding] = useState(false);
  // Playing to a beat is on where the studio can add a band; anyone can turn it on.
  useEffect(() => setBeatOn(studioOn()), []);

  const clearTimers = () => { timers.current.forEach(clearInterval); timers.current = []; cancelAnimationFrame(beatFrame.current); };
  function stopStream() {
    if (meter.current) { cancelAnimationFrame(meter.current.frame); meter.current.context.close().catch(() => {}); meter.current = null; }
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  }
  function startMeter(media) {
    try {
      const context = new (window.AudioContext || window.webkitAudioContext)(), analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      context.createMediaStreamSource(media).connect(analyser);
      context.resume().catch(() => {});
      const samples = new Float32Array(analyser.fftSize);
      const draw = () => {
        analyser.getFloatTimeDomainData(samples);
        let peak = 0;
        for (const value of samples) peak = Math.max(peak, Math.abs(value));
        if (meterBar.current) { meterBar.current.style.width = `${Math.min(100, Math.round(peak * 100))}%`; meterBar.current.toggleAttribute("data-clipping", peak > 0.97); }
        meter.current.frame = requestAnimationFrame(draw);
      };
      meter.current = { context, frame: requestAnimationFrame(draw) };
    } catch { /* The level meter is optional. */ }
  }

  // Start (or switch) the camera and microphone.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.isSecureContext) { setError("Recording needs a secure (https) connection."); setState("error"); return; }
      setState("starting"); stopStream();
      try {
        const media = await navigator.mediaDevices.getUserMedia({
          video: { ...(chosen.video ? { deviceId: { exact: chosen.video } } : { facingMode: "user" }), width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
          audio: { ...(chosen.audio ? { deviceId: { exact: chosen.audio } } : {}), echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        });
        if (cancelled) { media.getTracks().forEach((track) => track.stop()); return; }
        stream.current = media;
        live.current.srcObject = media;
        startMeter(media);
        const video = media.getVideoTracks()[0]?.getSettings() || {}, audio = media.getAudioTracks()[0]?.getSettings() || {};
        setActive({ video: video.deviceId || "", audio: audio.deviceId || "", mirrored: video.facingMode !== "environment" });
        const list = await navigator.mediaDevices.enumerateDevices();
        if (!cancelled) { setDevices({ video: list.filter((item) => item.kind === "videoinput"), audio: list.filter((item) => item.kind === "audioinput") }); setState("ready"); }
      } catch (failure) {
        if (!cancelled) { setError(FAILURES[failure?.name] || "Couldn’t start the camera."); setState("error"); }
      }
    })();
    return () => { cancelled = true; };
  }, [chosen.video, chosen.audio]);
  // The camera turns off when the recorder closes.
  useEffect(() => () => { clearTimers(); if (recorder.current?.state === "recording") recorder.current.stop(); stopStream(); }, []);
  useEffect(() => () => { if (take) URL.revokeObjectURL(take.url); }, [take]);

  function start() {
    setError("");
    if (!beatOn) { countIn(); return; }
    const bpm = Number(tempo);
    if (!(bpm >= SLOWEST && bpm <= FASTEST)) { setError(`Choose a tempo from ${SLOWEST} to ${FASTEST} BPM.`); return; }
    // Recording starts now; the count-in flashes a bar from LEAD_MS on, then the beat carries on.
    record({ tempo: bpm, beats });
    const begun = performance.now();
    plan.current = { begun, first: begun + LEAD_MS, period: 60000 / bpm, beats, tempo: bpm };
    setBeat(-1); setState("countdown");
    const tick = () => {
      const { first, period } = plan.current, index = Math.floor((performance.now() - first) / period);
      setBeat((shown) => (shown === index ? shown : index));
      if (index >= beats) setState((current) => (current === "countdown" ? "recording" : current));
      beatFrame.current = requestAnimationFrame(tick);
    };
    beatFrame.current = requestAnimationFrame(tick);
  }
  function countIn() {
    setCount(COUNT_IN); setState("countdown"); plan.current = null;
    let left = COUNT_IN;
    timers.current.push(setInterval(() => {
      left -= 1;
      if (left > 0) setCount(left);
      else { clearTimers(); record(); setState("recording"); }
    }, 1000));
  }
  function record(beatPlan = null) {
    const [mimeType] = FORMATS.find(([type]) => MediaRecorder.isTypeSupported(type)) || [""];
    const chunks = [], media = new MediaRecorder(stream.current, { ...(mimeType ? { mimeType } : {}), videoBitsPerSecond: 5_000_000, audioBitsPerSecond: 192_000 });
    media.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data); };
    media.onstop = () => {
      const type = (media.mimeType || mimeType || "video/webm").split(";")[0], blob = new Blob(chunks, { type }), timed = plan.current;
      if (!blob.size) { setError("Nothing was recorded. Try again."); setState("ready"); return; }
      if (blob.size > maxBytes) { setError(`That take is over ${maxBytes / 1024 / 1024} MB. Record a shorter one.`); setState("ready"); return; }
      setTake({ blob, extension: type.includes("mp4") ? "mp4" : "webm", url: URL.createObjectURL(blob), seconds: (Date.now() - startedAt.current) / 1000,
        ...(beatPlan && timed ? { beat: { tempo: beatPlan.tempo, beats: beatPlan.beats }, countIn: (timed.first - timed.begun) / 1000 } : {}) });
      setState("review");
    };
    media.start(1000);
    recorder.current = media; startedAt.current = Date.now(); setElapsed(0);
    timers.current.push(setInterval(() => {
      const seconds = (Date.now() - startedAt.current) / 1000;
      setElapsed(seconds);
      if (seconds >= MAX_SECONDS) stop();
    }, 250));
  }
  function stop() {
    clearTimers(); setBeat(-1);
    if (recorder.current?.state === "recording") recorder.current.stop(); else setState("ready");
  }
  function retake() { setTake(null); setBanding(false); setError(""); setState("ready"); }
  function use() {
    const stamp = new Date().toISOString().slice(0, 16).replace("T", "-").replace(":", "");
    onUse(new File([take.blob], `recording-${stamp}${take.band ? "-band" : ""}.${take.extension}`, { type: take.blob.type }));
  }
  // The tempo from taps: the average gap between the last few, within two seconds of each other.
  function tap() {
    const at = performance.now(), recent = [...taps.current.filter((time) => at - time < 2000), at].slice(-6);
    taps.current = recent;
    if (recent.length >= 3) setTempo(Math.max(SLOWEST, Math.min(FASTEST, Math.round(60000 / ((recent.at(-1) - recent[0]) / (recent.length - 1))))));
  }
  // A take with a band, written as one video (BandForTake.js); the take as it was is kept to go back to.
  function banded({ blob, extension, label }) {
    if (blob.size > maxBytes) { setError(`With the band, that video is over ${maxBytes / 1024 / 1024} MB. Record a shorter one.`); setBanding(false); return; }
    setTake((current) => ({ ...current, blob, extension, url: URL.createObjectURL(blob), band: label, original: current.original || { blob: current.blob, extension: current.extension } }));
    setBanding(false);
  }
  function withoutBand() { setTake((current) => ({ ...current, ...current.original, url: URL.createObjectURL(current.original.blob), band: null, original: null })); }
  const choose = (kind) => (event) => setChosen((current) => ({ ...current, [kind]: event.target.value }));
  const period = plan.current?.period || 500, inBar = beat >= 0 ? beat % beats : -1;

  if (state === "review" && take && banding) return <div className={styles.recorder}>
    <BandForTake take={take} idea={idea} formats={FORMATS} onUse={banded} onBack={() => setBanding(false)} />
  </div>;
  return <div className={styles.recorder} role="group" aria-label="Record a video">
    <div className={styles.recorderStage}>
      <video ref={live} className={active.mirrored ? styles.mirrored : undefined} autoPlay muted playsInline aria-label="Camera preview" hidden={["review", "error"].includes(state)} />
      {state === "review" && take && <video src={take.url} controls playsInline aria-label="Your recording" />}
      {state === "starting" && <p className={styles.recorderMessage}>Starting the camera…</p>}
      {state === "error" && <p className={styles.recorderMessage} role="alert">{error}</p>}
      {state === "countdown" && !plan.current && <span className={styles.countdown} aria-live="assertive">{count}</span>}
      {/* The count-in, and then the beat: silent flashes, the first of each bar brighter. */}
      {state === "countdown" && plan.current && (beat < 0 ? <span className={styles.beatCount} role="status">Ready…</span>
        : <span key={beat} className={styles.beatCount} style={{ "--beat": `${Math.round(period * 0.8)}ms` }} aria-live="assertive">{beat + 1}</span>)}
      {state === "recording" && plan.current && beat >= 0 && <>
        <span key={beat} className={styles.beatLight} data-accent={inBar === 0 ? "" : undefined} style={{ "--beat": `${Math.round(period * 0.7)}ms` }} aria-hidden="true" />
        <span className={styles.beatDots} aria-hidden="true">{Array.from({ length: beats }, (_, index) => <i key={index} data-on={index === inBar ? "" : undefined} />)}</span>
      </>}
      {state === "recording" && <span className={styles.recordingBadge} role="status"><span aria-hidden="true" />Recording {clock(elapsed)}</span>}
    </div>
    {["ready", "countdown", "recording"].includes(state) && <div className={styles.meter} title="Microphone level"><span ref={meterBar} /></div>}
    {state === "ready" && (devices.video.length > 1 || devices.audio.length > 1) && <div className={styles.recorderDevices}>
      {devices.video.length > 1 && <label>Camera<select value={chosen.video || active.video} onChange={choose("video")}>{devices.video.map((item, index) => <option key={item.deviceId} value={item.deviceId}>{item.label || `Camera ${index + 1}`}</option>)}</select></label>}
      {devices.audio.length > 1 && <label>Microphone<select value={chosen.audio || active.audio} onChange={choose("audio")}>{devices.audio.map((item, index) => <option key={item.deviceId} value={item.deviceId}>{item.label || `Microphone ${index + 1}`}</option>)}</select></label>}
    </div>}
    {state === "ready" && <div className={styles.beatSettings}>
      <label className={styles.beatToggle}><input type="checkbox" checked={beatOn} onChange={(event) => setBeatOn(event.target.checked)} />Count me in and keep the beat</label>
      {beatOn && <div className={styles.beatFields}>
        <label>Tempo (BPM)<input type="number" inputMode="numeric" min={SLOWEST} max={FASTEST} value={tempo} onChange={(event) => setTempo(event.target.value)} /></label>
        <button type="button" onClick={tap}>Tap</button>
        <label>Beats in a bar<select value={beats} onChange={(event) => setBeats(Number(event.target.value))}>{[2, 3, 4, 5, 6, 7].map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
      </div>}
    </div>}
    {error && state !== "error" && <p className={styles.error} role="alert">{error}</p>}
    {state === "review" && take && <p className={styles.hint}>{clock(take.seconds)} &middot; {(take.blob.size / 1024 / 1024).toFixed(1)} MB{take.band ? ` · with ${take.band}` : ""}</p>}
    <div className={styles.formActions}>
      <button type="button" onClick={onCancel}>Cancel</button>
      {state === "ready" && <button type="button" className={styles.primary} onClick={start}>Start recording</button>}
      {["countdown", "recording"].includes(state) && <button type="button" className={styles.primary} onClick={stop}>Stop recording</button>}
      {state === "review" && <>
        <button type="button" onClick={retake}>Retake</button>
        {take?.beat && studioOn() && !take.band && <button type="button" onClick={() => setBanding(true)}><FiMusic aria-hidden="true" /> Add a band</button>}
        {take?.band && <button type="button" onClick={withoutBand}>Remove the band</button>}
        <button type="button" className={styles.primary} onClick={use}>Use this video</button>
      </>}
    </div>
    {["ready", "starting"].includes(state) && <p className={styles.hint}>{beatOn
      ? `A bar of silent flashes counts you in, then the beat keeps flashing while you play.${studioOn() ? " Afterwards TrueChops can add a band, in time and in your key." : ""} A take can run up to ${MAX_SECONDS / 60} minutes.`
      : `Recording starts after a ${COUNT_IN}-second count-in, and can run up to ${MAX_SECONDS / 60} minutes.`}</p>}
  </div>;
}
